import { CompositionEventsEnum } from "../librettoEditor/CompositionEvents";
import { SequenceStateStore } from "./store/SequenceStateStore";
import {SequenceCompositionService} from "./services/SequenceCompositionService";
import {updateAdjacency} from "../librettoEditor/modules/timeline/utils/updateLayerClipsAdjacency";
import {SequenceApplicationStore} from "./store/SequenceApplicationStore";
import { SequencePersistenceService } from "./services/SequencePersistenceService";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import { assignWaveformColor, getUsedWaveformColors } from "./utils/waveformColors";

/**
 * Binds a specific Composition instance (modal-scoped) to the SequenceStateStore.
 * Mirrors the main CompositionBridge but writes to the sequence store.
 */
class SequenceCompositionBridge {
  private initialized = false;
  private isRestoring = false; // Flag to skip persistence during restoration

  init() {
    if (this.initialized) return;
    this.setupEventListeners();
  }

  setRestoring(restoring: boolean) {
    this.isRestoring = restoring;
  }

  private setupEventListeners() {
    const c = SequenceCompositionService.composition;
    c.on(CompositionEventsEnum.LAYER_ADDED, this.handleLayerAdded);
    c.on(CompositionEventsEnum.LAYER_REMOVED, this.handleLayerRemoved);
    c.on(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
    c.on(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
    c.on(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
    c.on(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
    c.on(CompositionEventsEnum.PLAYBACK_STATE, this.handlePlaying);
    c.on(CompositionEventsEnum.PLAYBACK_TIME, this.handleTime);
    this.initialized = true;
  }

  private handleLayerAdded = (event: any) => {
    const payload = event.detail;
    SequenceStateStore.addLayer({ id: payload.layer.id, clipIds: [] });
    // Note: Layer name will be set in handleClipAdded when the first clip is added
    if (!this.isRestoring) {
      SequencePersistenceService.addLayer(payload.layer.id);
      // Persist ordered layer ids as a whole after each mutation
      SequencePersistenceService.setOrderLayerIds([...SequenceStateStore.orderedLayerIds]);
    }
  }

  private handleLayerRemoved = (event: any) => {
    const payload = event.detail;
    SequenceStateStore.removeLayer(payload.layerId);
    if (!this.isRestoring) {
      SequencePersistenceService.deleteLayer(payload.layerId);
      // Persist ordered layer ids as a whole after each mutation
      SequencePersistenceService.setOrderLayerIds([...SequenceStateStore.orderedLayerIds]);
    }
  }

  private handleClipRemoved = (event: any) => {
      const payload = event.detail;
      SequenceApplicationStore.removeClipFromSelection(payload.clipId);

      SequenceStateStore.removeClip(payload.layerId, payload.clipId);
      updateAdjacency(payload.layerId);
      if (!this.isRestoring) {
        SequencePersistenceService.deleteClip(payload.layerId, payload.clipId);
        SequencePersistenceService.setOrderedClipIds(
          payload.layerId,
          [...(SequenceStateStore.layers?.[payload.layerId]?.clipIds ?? [])]
        );
      }
  }

  private handleClipUpdated = (event: any) => {
    const payload = event.detail;
    const clip = payload.clip;
    try {
      const isMedia = 'range' in clip && 'offset' in clip;
      let startTime = 0;
      let duration = 0;
      let trimmedDuration = 0;

      if (isMedia) {
        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? 0;
        const mediaDur = clip.duration?.seconds ?? Math.max(0, outSec - inSec);
        startTime = clip.start?.seconds ?? 0;
        trimmedDuration = clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec);
        duration = mediaDur;
      } else {
        const s = clip.start?.seconds ?? 0;
        const e = clip.stop?.seconds ?? s;
        startTime = s;
        duration = Math.max(0, e - s);
        trimmedDuration = clip.timelineDuration?.seconds ?? duration;
      }

      SequenceStateStore.updateClip(clip.id, {
        id: clip.id,
        type: String(clip.type ?? 'unknown'),
        startTime,
        duration,
        trimmedDuration,
        hasPredecessor: false,
        hasSuccessor: false,
      });
      try { SequenceStateStore.setDuration(SequenceCompositionService.getFitDuration()); } catch {}
      if (!this.isRestoring) {
        SequencePersistenceService.updateClip(clip);
      }
    } catch (e) {
      console.warn('[SequenceBridge] handleClipUpdated failed', e);
    }
  }

  private handleClipAdded = (event: any) => {
    const payload = event.detail;
    const clip = payload.clip;
    const layerId = payload.layerId;
    try {
      const isMedia = 'range' in clip && 'offset' in clip;
      let startTime = 0;
      let duration = 0;
      let trimmedDuration = 0;

      if (isMedia) {
        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? 0;
        const mediaDur = clip.duration?.seconds ?? Math.max(0, outSec - inSec);
        startTime = clip.start?.seconds ?? 0;
        trimmedDuration = clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec);
        duration = mediaDur;
      } else {
        const s = clip.start?.seconds ?? 0;
        const e = clip.stop?.seconds ?? s;
        startTime = s;
        duration = Math.max(0, e - s);
        trimmedDuration = clip.timelineDuration?.seconds ?? duration;
      }

      // Assign waveform color if not already assigned (and not restoring)
      let waveformColor: string | undefined;
      if (!this.isRestoring) {
        const existingColor = SequenceStateStore.getWaveformColor(clip.id);
        if (!existingColor) {
          // Get currently used colors and assign an unused one
          const usedColors = getUsedWaveformColors(SequenceStateStore.clips);
          waveformColor = assignWaveformColor(usedColors);
          SequenceStateStore.setWaveformColor(clip.id, waveformColor);
        } else {
          waveformColor = existingColor;
        }
      } else {
        // During restoration, color should come from persistence
        waveformColor = SequenceStateStore.getWaveformColor(clip.id);
      }

      SequenceStateStore.addClip(layerId, {
        id: clip.id,
        type: String(clip.type ?? 'unknown'),
        startTime,
        duration,
        trimmedDuration,
        hasPredecessor: false,
        hasSuccessor: false,
        waveformColor,
      });

      // Set the clip title from clip.name if available
      if (clip.name) {
        SequenceStateStore.setTitleForClip(clip.id, clip.name);
      }

      // If this is the first clip on the layer, set the layer name from the clip's title
      // Skip during restoration as layer names are restored from persistence
      if (!this.isRestoring) {
        const layer = SequenceStateStore.layers[layerId];
        if (layer && layer.clipIds.length === 1) {
          // Only set if layer name doesn't already exist (from persistence)
          const existingName = SequenceStateStore.getLayerName(layerId);
          if (!existingName) {
            let clipTitle = SequenceStateStore.clipIdToTitle[clip.id];
            if (!clipTitle) {
                clipTitle = clip.name;
            }
            if (clipTitle) {
              SequenceStateStore.setLayerName(layerId, clipTitle);
              SequencePersistenceService.updateLayerName(layerId, clipTitle);
            }
          }
        }
      }

      try { SequenceStateStore.setDuration(SequenceCompositionService.getFitDuration()); } catch {}
      if (!this.isRestoring) {
        SequencePersistenceService.addClip(clip, layerId);
        SequencePersistenceService.setOrderedClipIds(
          layerId,
          [...(SequenceStateStore.layers?.[layerId]?.clipIds ?? [])]
        );
      }
    } catch (e) {
      console.warn('[SequenceBridge] handleClipAdded failed', e);
    }
  }

  private handleCanvasResized = (event: any) => {
    const payload = event.detail;
    SequenceStateStore.setDisplayResolution(payload.width, payload.height);
  }

  private handlePlaying = (event: any) => {
    const payload = event.detail;
    SequenceStateStore.setIsPlaying(payload.isPlaying);
  }

  private handleTime = (event: any) => {
    const payload = event.detail;
    SequenceStateStore.setCurrentTime(payload.time);
  }

  dispose() {
    if (!this.initialized) return;
    const c = SequenceCompositionService.composition;
    if (c) {
      c.off(CompositionEventsEnum.LAYER_ADDED);
      c.off(CompositionEventsEnum.LAYER_REMOVED);
      c.off(CompositionEventsEnum.CLIP_ADDED);
      c.off(CompositionEventsEnum.CLIP_UPDATED);
      c.off(CompositionEventsEnum.CLIP_REMOVED);
      c.off(CompositionEventsEnum.CANVAS_RESIZED);
      c.off(CompositionEventsEnum.PLAYBACK_STATE);
      c.off(CompositionEventsEnum.PLAYBACK_TIME);
    }
    this.initialized = false;
    this.isRestoring = false;
  }

    static init() {
        return undefined;
    }
}

export default new SequenceCompositionBridge();
